import { useCallback, useRef, useState } from 'react';

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;'
}[character] ?? character));

const safeLink = (url: string): string | undefined => {
    try {
        const parsed = new URL(url, window.location.origin);
        return ['http:', 'https:', 'mailto:'].includes(parsed.protocol) ? parsed.href : undefined;
    } catch {
        return undefined;
    }
};


const normalizeWhitespace = (text: string): string =>
    text
        .replace(/[\u00A0\u202F\u2007]/g, ' ')
        .replace(/\u2060/g, '');


const legacyHtmlToMarkdown = (value: string): string => {
    if (!/[<][a-z!/][\s\S]*[>]/i.test(value)) {
        return value;
    }

    const document = new DOMParser().parseFromString(value, 'text/html');
    const convert = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent ?? '';
        }
        if (!(node instanceof HTMLElement)) {
            return '';
        }

        const content = Array.from(node.childNodes).map(convert).join('');
        switch (node.tagName.toLowerCase()) {
            case 'br': return '\n';
            case 'p': case 'div': return `${content}\n\n`;
            case 'h1': case 'h2': case 'h3': return `${'#'.repeat(Number(node.tagName[1]))} ${content}\n\n`;
            case 'strong': case 'b': return `**${content}**`;
            case 'em': case 'i': return `*${content}*`;
            case 's': case 'strike': case 'del': return `~~${content}~~`;
            case 'li': return `- ${content}\n`;
            case 'ul': case 'ol': return `${content}\n`;
            case 'a': {
                const url = safeLink(node.getAttribute('href') ?? '');
                return url ? `[${content}](${url})` : content;
            }
            default: return content;
        }
    };

    return normalizeWhitespace(
        Array.from(document.body.childNodes).map(convert).join('').trim()
    );
};

const parseInlineMarkdown = (text: string): string => {
    let result = escapeHtml(text);

    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label: string, url: string) => {
        const safeUrl = safeLink(url.trim());
        return safeUrl
            ? `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${label}</a>`
            : label;
    });
    result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    result = result.replace(/__(.+?)__/g, '<strong>$1</strong>');
    result = result.replace(/~~(.+?)~~/g, '<s>$1</s>');
    result = result.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    result = result.replace(/(?<![a-zA-Z])_([^_]+)_(?![a-zA-Z])/g, '<em>$1</em>');

    return result;
};

/**
 * Converts the deliberately small Markdown subset used by this client to safe HTML.
 * User-supplied HTML is escaped before formatting is applied.
 */
export const renderMarkdown = (markdown: string): string => {
    if (!markdown) {
        return '';
    }

    markdown = normalizeWhitespace(legacyHtmlToMarkdown(markdown));
    markdown = markdown.replace(/\u00A0/g, ' ')

    const html: string[] = [];
    let paragraph: string[] = [];
    let listType: 'ol' | 'ul' | undefined;

    const flushParagraph = () => {
        const content = paragraph.join(' ').trim();
        if (content) {
            html.push(`<p style="word-break: normal; overflow-wrap: break-word;">${parseInlineMarkdown(content)}</p>`);
        }
        paragraph = [];
    };
    const closeList = () => {
        if (listType) {
            html.push(`</${listType}>`);
            listType = undefined;
        }
    };

    for (const line of markdown.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) {
            flushParagraph();
            closeList();
            continue;
        }

        if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
            flushParagraph();
            closeList();
            html.push('<hr>');
            continue;
        }

        const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (heading) {
            flushParagraph();
            closeList();
            html.push(`<h${heading[1].length}>${parseInlineMarkdown(heading[2])}</h${heading[1].length}>`);
            continue;
        }

        const unordered = trimmed.match(/^[-*]\s+(.+)$/);
        const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
        const nextListType = unordered ? 'ul' : ordered ? 'ol' : undefined;
        if (nextListType) {
            flushParagraph();
            if (listType !== nextListType) {
                closeList();
                listType = nextListType;
                html.push(`<${listType}>`);
            }
            html.push(`<li>${parseInlineMarkdown(unordered?.[1] ?? ordered?.[1] ?? '')}</li>`);
            continue;
        }

        closeList();
        paragraph.push(trimmed);
    }

    flushParagraph();
    closeList();
    return html.join('\n');
};

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    id?: string;
    ariaLabel?: string;
}

type MarkdownFormat = 'bold' | 'italic' | 'strikethrough' | 'h1' | 'h2' | 'h3' | 'ul' | 'ol' | 'link' | 'hr';

/**
 * A limited Markdown editor. It stores Markdown rather than HTML and has no HTML import path.
 */
export const RichTextEditor = ({ value, onChange, placeholder, id, ariaLabel }: RichTextEditorProps) => {
    const [showPreview, setShowPreview] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const markdownValue = normalizeWhitespace(legacyHtmlToMarkdown(value));

    const insertFormat = useCallback((format: MarkdownFormat) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = markdownValue.slice(start, end);
        let insertion: string;
        let cursorOffset: number;

        switch (format) {
            case 'bold':
                insertion = `**${selectedText || 'bold text'}**`;
                cursorOffset = selectedText ? insertion.length : 2;
                break;
            case 'italic':
                insertion = `*${selectedText || 'italic text'}*`;
                cursorOffset = selectedText ? insertion.length : 1;
                break;
            case 'strikethrough':
                insertion = `~~${selectedText || 'strikethrough text'}~~`;
                cursorOffset = selectedText ? insertion.length : 2;
                break;
            case 'h1':
            case 'h2':
            case 'h3':
                insertion = `${'#'.repeat(Number(format[1]))} ${selectedText || `Heading ${format[1]}`}`;
                cursorOffset = insertion.length;
                break;
            case 'ul':
                insertion = `- ${selectedText || 'List item'}`;
                cursorOffset = insertion.length;
                break;
            case 'ol':
                insertion = `1. ${selectedText || 'List item'}`;
                cursorOffset = insertion.length;
                break;
            case 'link':
                insertion = `[${selectedText || 'link text'}](https://example.com)`;
                cursorOffset = selectedText ? insertion.length - 20 : 1;
                break;
            case 'hr':
                insertion = '\n---\n';
                cursorOffset = insertion.length;
                break;
        }

        onChange(`${markdownValue.slice(0, start)}${insertion}${markdownValue.slice(end)}`);
        requestAnimationFrame(() => {
            const position = start + cursorOffset;
            textarea.focus();
            textarea.setSelectionRange(position, position);
        });
    }, [markdownValue, onChange]);

    const toolbarButton = (format: MarkdownFormat, label: string, text: string) => (
        <button type="button" onClick={() => insertFormat(format)} aria-label={label} title={label}>{text}</button>
    );

    return (
        <div className="markdown-editor">
            <div className="markdown-editor__toolbar" role="toolbar" aria-label="Markdown formatting">
                {toolbarButton('h1', 'Heading 1', 'H1')}
                {toolbarButton('h2', 'Heading 2', 'H2')}
                {toolbarButton('h3', 'Heading 3', 'H3')}
                {toolbarButton('bold', 'Bold', 'B')}
                {toolbarButton('italic', 'Italic', 'I')}
                {toolbarButton('strikethrough', 'Strikethrough', 'S')}
                {toolbarButton('ul', 'Bulleted list', '• List')}
                {toolbarButton('ol', 'Numbered list', '1. List')}
                {toolbarButton('link', 'Insert link', 'Link')}
                {toolbarButton('hr', 'Horizontal rule', 'HR')}
                <button type="button" className="markdown-editor__preview-toggle" onClick={() => setShowPreview((preview) => !preview)}>
                    {showPreview ? 'Edit' : 'Preview'}
                </button>
            </div>
            {showPreview ? (
                <RichTextDisplay value={value} className="markdown-editor__preview" />
            ) : (
                <textarea
                    ref={textareaRef}
                    id={id}
                    className="markdown-editor__textarea"
                    value={markdownValue}
                    onChange={(event) => onChange(normalizeWhitespace(event.target.value))}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                />
            )}
            <small className="markdown-editor__help">Supports headings, bold, italic, strikethrough, lists, links, and horizontal rules.</small>
        </div>
    );
};

interface RichTextDisplayProps {
    value: string;
    className?: string;
}

export const RichTextDisplay = ({ value, className }: RichTextDisplayProps) => (
    <div
        className={className ? `markdown-content ${className}` : 'markdown-content'}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
    />
);
